<template>
  <v-main class="investments d-flex justify-center pt-0" role="presentation">
    <v-container>
      <v-row align="center" justify="center">
        <v-col cols="12" class="pt-0">
          <InvestmentsTitle :is-dashboard="false" />
          <v-sheet style="background: transparent">
            <div v-if="investmentsLoading" />
            <v-tabs
              v-else
              v-model="selectedTab"
              class="investments-tabs"
              :style="{ '--investments-tab-width': tabWidth }"
              :class="{
                'calculator': calculatorEnabled,
                'has-investments': hasInvestments || investmentsLoading,
              }"
            >
              <v-tab
                value="my-investments"
                v-show="hasInvestments"
                :class="selectedTab === 'my-investments' ? 'colorselected' : 'colorunselected'"
                class="pl-1 pr-1 wrapText tabSize"
              >
                <div :class="$vuetify.display.xs ? 'fs11' : 'fs16'" class="wrapText">
                  {{ $t('L_my_investments') }}
                </div>
              </v-tab>
              <v-tab
                value="retirement-calculator"
                :class="
                  selectedTab === 'retirement-calculator' ? 'colorselected' : 'colorunselected'
                "
                class="pl-1 pr-1 wrapText tabSize"
                v-if="calculatorEnabled"
              >
                <div :class="$vuetify.display.xs ? 'fs11' : 'fs16'" class="wrapText">
                  {{ $t('L_guidance_calculator') }}
                </div>
              </v-tab>
              <v-tab
                value="learn-more"
                :class="selectedTab === 'learn-more' ? 'colorselected' : 'colorunselected'"
                class="pl-1 pr-1 wrapText tabSize"
              >
                <div :class="$vuetify.display.xs ? 'fs11' : 'fs16'" class="wrapText">
                  {{ $t('L_learn_more') }}
                </div>
              </v-tab>
            </v-tabs>
            <v-tabs-window v-model="selectedTab" touchless>
              <LoadingIndicator :loading="investmentsLoading" v-if="investmentsLoading" />
              <v-tabs-window-item v-if="hasInvestments" value="my-investments">
                <MyInvestments v-if="hasInvestments" />
                <div v-else style="height: 300px" />
              </v-tabs-window-item>
              <v-tabs-window-item v-if="calculatorEnabled" value="retirement-calculator">
                <GuidanceCalculator @open="viewMyInvestments" />
              </v-tabs-window-item>
              <v-tabs-window-item value="learn-more">
                <LearnMore />
              </v-tabs-window-item>
            </v-tabs-window>
          </v-sheet>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import GuidanceCalculator from './components/GuidanceCalculator.vue';
import { mapActions, mapState } from 'pinia';
import { RouteNames } from '@/router/routeNames';
import { useGlobalStore } from '@/stores/global/global.store';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { FEATURE_FLAGS } from '@/shared/models/constants.models';
import InvestmentsTitle from '@/modules/home/dashboard/components/Investments/InvestmentsTitle.vue';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import LearnMore from './components/LearnMore.vue';
import MyInvestments from './components/MyInvestments.vue';

export default defineComponent({
  name: 'InvestmentsCard',
  components: {
    LearnMore,
    MyInvestments,
    InvestmentsTitle,
    LoadingIndicator,
    GuidanceCalculator,
  },
  data() {
    return {
      selectedTab: 'my-investments',
    };
  },
  computed: {
    ...mapState(useGlobalStore, ['containsFeature', 'features']),
    ...mapState(useCustomerStore, ['investments', 'loading']),
    calculatorEnabled() {
      return this.containsFeature(FEATURE_FLAGS.RETIREMENT_CALCULATOR);
    },
    hasInvestments() {
      return (this.investments.summary?.totalValue ?? 0) > 0;
    },
    investmentsLoading() {
      return this.loading.investments;
    },
    visibleTabsCount() {
      return 1 + Number(this.hasInvestments) + Number(this.calculatorEnabled);
    },
    tabWidth() {
      return `${100 / this.visibleTabsCount}%`;
    },
  },
  async created() {
    if (!this.hasInvestments) {
      await this.getInvestments();
    }
    if (this.investmentsLoading) {
      return;
    }
    const newName = this.$router.currentRoute.value.name;
    if (newName) {
      if (this.hasInvestments) {
        if (newName === RouteNames.INVESTMENT_CALCULATOR) {
          this.selectedTab = 'retirement-calculator';
        } else if (newName === RouteNames.INVESTMENT_LEARN_MORE) {
          this.selectedTab = 'learn-more';
        } else {
          this.selectedTab = 'my-investments';
        }
      } else {
        if (newName === RouteNames.INVESTMENT_CALCULATOR) {
          this.selectedTab = 'retirement-calculator';
        } else {
          this.selectedTab = 'learn-more';
        }
      }
    } else {
      if (this.hasInvestments) {
        this.selectedTab = 'my-investments';
      } else {
        this.selectedTab = 'learn-more';
      }
    }
  },
  methods: {
    ...mapActions(useCustomerStore, ['getInvestments']),
    viewMyInvestments() {
      this.selectedTab = 'my-investments';
    },
  },
  watch: {
    investmentsLoading(newData) {
      if (!newData) {
        if (this.hasInvestments) {
          this.selectedTab = 'my-investments';
        } else {
          this.selectedTab = 'retirement-calculator';
        }
      }
    },
  },
});
</script>

<style lang="scss">
.wrapText {
  white-space: break-spaces !important;
  word-break: break-word;
  text-wrap-mode: wrap;
}
.tabSize {
  min-width: 20px !important;
}
.fs11 {
  font-size: 11px !important;
}
.fs16 {
  font-size: 16px !important;
}
.colorselected {
  background-color: #e2f0fe !important;
  color: #063562 !important;
}
.colorunselected {
  color: #000000 !important;
  border-bottom: 4px solid #cccccc !important;
}
.investments {
  padding: 24px 0 0 !important;
  margin: 0 auto 60px auto;
  max-width: 1024px;
  .v-row {
    margin: 0;
  }

  .v-window__container {
    /* Example fix if something is pushing the container left */
    margin-left: 0 !important;
    padding-left: 0 !important;
    transform: none !important;
    position: relative !important;
  }

  .v-tabs {
    &.investments-tabs {
      .v-slide-group__content {
        width: 100%;
      }

      .v-tab {
        flex: 0 0 var(--investments-tab-width) !important;
        max-width: var(--investments-tab-width) !important;
        min-width: var(--investments-tab-width) !important;
      }
    }

    .v-tab .v-tab__slider {
      height: 4px;
      background-color: #063562 !important;
    }
  }

  .wrapText {
    white-space: normal !important;
    overflow-wrap: anywhere;
    text-align: center;
    line-height: 1.2;
  }
  .v-theme--primerica.v-table .v-table__wrapper THEAD TR,
  .v-theme--primerica.v-table .v-table__wrapper THEAD TR TH,
  .v-theme--primerica.v-table.v-table--fixed-header > .v-table__wrapper > TABLE > THEAD > TR > TH {
    background: transparent !important;
  }
  .v-table__wrapper th:nth-child(3) .v-data-table-header__content {
    justify-content: flex-end;
  }
}
</style>
